/**
 * /coach/activity — Owner-only audit log viewer.
 *
 * Coaches see only their own actions (filtered server-side).
 * Owners see everyone. Filter by action type or actor.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Activity, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
    "lead.status_change": "Status change",
    "lead.contact_logged": "Contact logged",
    "athlete.notes_edit": "Athlete notes edited",
    "billing.alert_acknowledge": "Billing alert ack",
    "billing.refund": "Refund issued",
    "assessment.recap_sent": "Recap sent",
    "assessment.cancelled": "Assessment cancelled",
    "billing.plan_created": "Plan created",
    "billing.plan_updated": "Plan updated",
    "billing.pack_created": "Pack created",
    "billing.pack_updated": "Pack updated",
    "coach.invited": "Coach invited",
    "coach.permissions_changed": "Permissions changed",
    "coach.revoked": "Coach revoked",
    "announcement.sent": "Announcement sent",
    "strike.forgiven": "Strike forgiven",
    "strike.recredited": "Session re-credited",
    "attendance.manual_deleted": "Manual log deleted",
    "attendance.scheduled_reversed": "Check-in reversed",
};

const ACTION_TONE = {
    "billing.refund": "border-amber-500/40 bg-amber-500/10 text-amber-300",
    "billing.alert_acknowledge": "border-brand-red/40 bg-brand-red/10 text-brand-red",
    "coach.revoked": "border-brand-red/40 bg-brand-red/10 text-brand-red",
    "strike.forgiven": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    "attendance.manual_deleted": "border-amber-500/40 bg-amber-500/10 text-amber-300",
    "attendance.scheduled_reversed": "border-amber-500/40 bg-amber-500/10 text-amber-300",
};

export default function ActivityLog() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [actions, setActions] = useState([]);
    const [actionFilter, setActionFilter] = useState("");
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        if (!authLoading && (!user || !["owner", "coach"].includes(user.role))) {
            navigate("/", { replace: true });
        }
    }, [authLoading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const params = actionFilter ? { action: actionFilter } : {};
            const r = await axios.get(`${API}/coach/audit-log`, { params, withCredentials: true });
            setEntries(r.data?.entries || []);
        } catch (e) {
            toast.error("Couldn't load activity", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const loadActions = async () => {
        try {
            const r = await axios.get(`${API}/coach/audit-log/actions`, { withCredentials: true });
            setActions(r.data?.actions || []);
        } catch {
            // silent — filter is non-critical
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter]);
    useEffect(() => { loadActions(); }, []);

    const grouped = useMemo(() => {
        // Group entries by date (YYYY-MM-DD) for visual rhythm
        const acc = {};
        for (const e of entries) {
            const key = (e.created_at || "").slice(0, 10) || "—";
            (acc[key] = acc[key] || []).push(e);
        }
        return Object.entries(acc);
    }, [entries]);

    if (authLoading || !user) return null;

    return (
        <div data-testid="activity-log" className="min-h-screen bg-brand-obsidian text-white">
            <section className="pt-28 pb-6 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <button
                        type="button"
                        onClick={() => navigate("/coach")}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5"
                    >
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </button>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-2 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        <Activity className="w-3 h-3" /> Activity log
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                        WHO DID <span className="text-brand-red">WHAT.</span>
                    </h1>
                    <p className="mt-3 text-sm text-zinc-300 max-w-2xl">
                        Every status flip, refund, recap, alert ack — recorded so you and any future
                        coach can trace exactly what happened.
                    </p>
                </div>
            </section>

            <section className="py-6 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10 flex items-center gap-3">
                    <Filter className="w-4 h-4 text-zinc-400" />
                    <select
                        data-testid="audit-action-filter"
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                    >
                        <option value="">All actions</option>
                        {actions.map((a) => (
                            <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                        ))}
                    </select>
                    <span className="text-[11px] text-zinc-400 ml-auto">{entries.length} entries</span>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    {busy ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-zinc-300">
                            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-10 text-center text-zinc-300">
                            <Activity className="w-7 h-7 text-zinc-700 mx-auto mb-3" />
                            Nothing logged yet for this filter.
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {grouped.map(([day, rows]) => (
                                <div key={day}>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2">
                                        {new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                                    </div>
                                    <ul className="space-y-2">
                                        {rows.map((e) => (
                                            <li
                                                key={e.audit_id}
                                                data-testid={`audit-row-${e.audit_id}`}
                                                className="border border-zinc-800 rounded-xl bg-zinc-950/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-[10px] tracking-[0.15em] uppercase border px-2 py-0.5 ${ACTION_TONE[e.action] || "border-zinc-700 bg-zinc-900/40 text-zinc-300"}`}>
                                                            {ACTION_LABELS[e.action] || e.action}
                                                        </span>
                                                        {e.target_label && (
                                                            <span className="text-sm text-white truncate">{e.target_label}</span>
                                                        )}
                                                    </div>
                                                    <div className="text-[11px] text-zinc-400 mt-1">
                                                        {e.actor_name || "—"}
                                                        {renderMeta(e)}
                                                    </div>
                                                </div>
                                                <div className="text-[11px] text-zinc-500 shrink-0 whitespace-nowrap">
                                                    {new Date(e.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function renderMeta(e) {
    const m = e.meta || {};
    if (e.action === "lead.status_change") {
        return <> · <span className="text-zinc-300">{m.from || "—"} → {m.to || "—"}</span></>;
    }
    if (e.action === "billing.refund") {
        const amt = (m.amount_cents || 0) / 100;
        return <> · <span className={m.ok === false ? "text-brand-red" : "text-amber-300"}>${amt.toFixed(2)} {m.ok === false ? "(failed)" : ""}</span></>;
    }
    if (e.action === "billing.alert_acknowledge") {
        const amt = (m.amount_cents || 0) / 100;
        return <> · <span className="text-zinc-300">${amt.toFixed(2)} alert cleared</span></>;
    }
    if (e.action === "assessment.recap_sent") {
        return <> · <span className="text-zinc-300">{m.sent_count || 0} sent · {m.skipped_count || 0} skipped</span></>;
    }
    return null;
}
